<template>
    <kiosk-team-details :teamId="props.teamId">
        <div
            class="vx-col-span-12 lg:vx-col-span-10 vx-flex vx-flex-col vx-w-full vx-max-w-screen-sm vx-justify-center"
        >
            <page-header>Details</page-header>

            <vx-expandable-card class="vx-mb-6">
                <template v-slot:content>
                    <div class="vx-flex vx-flex-col vx-gap-4">
                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Name:</strong>
                            <div class="vx-text-sm vx-text-slate-500 vx-flex vx-items-center vx-gap-1">
                                <vx-badge size="xs" color="slate">{{ team.id }}</vx-badge> {{ team.name }}
                            </div>
                        </div>
                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Status:</strong>
                            <div class="vx-text-sm vx-text-slate-500 vx-capitalize">
                                <vx-popover hover arrow placement="top">
                                    {{ team.status }}
                                    <template v-slot:content v-if="team.status === 'suspended'">
                                        <div class="vx-max-w-xs">
                                            {{ team.status_reason }}
                                        </div>
                                    </template>
                                </vx-popover>
                            </div>
                        </div>
                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Owner:</strong>
                            <div class="vx-text-sm vx-text-slate-500">
                                <vx-icon-loading spin v-if="ownerLoading"></vx-icon-loading>
                                <div v-else-if="owner" class="vx-flex vx-items-center vx-gap-2">
                                    <div>{{ owner.name }}</div>
                                    <vx-button
                                        color="muted"
                                        size="xs"
                                        data-test="go-to-owner"
                                        :href="`/kiosk/users/${owner.id}`"
                                    >
                                        Go to Owner <font-awesome-icon :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Users:</strong>
                            <div class="vx-text-sm vx-text-slate-500">
                                <vx-icon-loading spin v-if="usersCountLoading"></vx-icon-loading>
                                <div v-else-if="usersCount != undefined" class="vx-flex vx-items-center vx-gap-2">
                                    {{ usersCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Pending Invites:</strong>
                            <div class="vx-text-sm vx-text-slate-500">
                                <vx-icon-loading spin v-if="invitationsCountLoading"></vx-icon-loading>
                                <div v-else-if="invitationsCount != undefined" class="vx-flex vx-items-center vx-gap-2">
                                    {{ invitationsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Groups:</strong>
                            <div class="vx-text-sm vx-text-slate-500">
                                <vx-icon-loading spin v-if="groupsCountLoading"></vx-icon-loading>
                                <div v-else-if="groupsCount != undefined" class="vx-flex vx-items-center vx-gap-2">
                                    {{ groupsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Parent:</strong>
                            <div class="vx-text-sm vx-text-slate-500 vx-flex vx-items-center vx-gap-1">
                                <vx-icon-loading spin v-if="parentLoading"></vx-icon-loading>
                                <template v-else-if="parent">
                                    <vx-badge size="xs" color="slate">{{ parent.id }}</vx-badge>
                                    <span>{{ parent.name }}</span>
                                    <vx-button
                                        class="vx-ml-1"
                                        size="xs"
                                        color="muted"
                                        data-test="go-to-parent"
                                        :href="`/kiosk/teams/${parent.id}`"
                                    >
                                        Go to Parent
                                        <font-awesome-icon :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </template>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Sub Teams:</strong>
                            <div class="vx-text-sm vx-text-slate-500">
                                <vx-icon-loading spin v-if="teamsCountLoading"></vx-icon-loading>
                                <div v-else-if="teamsCount != undefined" class="vx-flex vx-items-center vx-gap-2">
                                    {{ teamsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Team Phone Numbers:</strong>
                            <div class="vx-text-sm vx-text-slate-500">
                                <vx-icon-loading spin v-if="phoneNumbersCountLoading"></vx-icon-loading>
                                <div
                                    v-else-if="phoneNumbersCount != undefined"
                                    class="vx-flex vx-items-center vx-gap-2"
                                >
                                    {{ phoneNumbersCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>

            <vx-expandable-card class="vx-mb-6">
                <template v-slot:content>
                    <div class="vx-text-xl vx-font-extrabold vx-mb-4">Resources</div>
                    <div class="vx-flex vx-flex-col vx-gap-4">
                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Phone Numbers:</strong>
                            <div class="vx-text-sm vx-text-slate-500 vx-flex vx-flex-col">
                                <vx-icon-loading spin v-if="phoneNumbersLoading"></vx-icon-loading>
                                <div v-else-if="!phoneNumbers.length">No phone numbers yet.</div>
                                <div v-for="phoneNumber in phoneNumbers" :key="phoneNumber.id">
                                    {{ formatPhoneNumber(phoneNumber.phone_number) }}
                                    {{ countryEmoji(phoneNumber.iso_country) }} &rarr;
                                    {{ NUMBER_TYPES[phoneNumber.number_type] }}
                                </div>
                            </div>
                            <div>
                                <vx-button
                                    color="muted"
                                    size="xs"
                                    :href="
                                        currentUserId !== team.owner_id
                                            ? `/spark/kiosk/users/impersonate/${team.owner_id}?team_id=${team.id}&redirect_to=/settings/teams/${team.id}#/phone-numbers`
                                            : `/settings/teams/${team.id}#/phone-numbers`
                                    "
                                >
                                    See all
                                    <font-awesome-icon :icon="faTheaterMasks"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </div>

                        <div class="vx-flex vx-flex-col vx-gap-1">
                            <strong>Short URL:</strong>
                            <div class="vx-text-sm vx-text-slate-500 vx-flex vx-flex-col">
                                {{ team.domain || '-' }}
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>

            <vx-expandable-card>
                <template v-slot:content>
                    <div class="vx-text-xl vx-font-extrabold vx-mb-4">Custom Attributes</div>
                    <vx-icon-loading spin v-if="customAttributesLoading"></vx-icon-loading>

                    <div v-else-if="!customAttributes.length" class="vx-text-slate-500">No custom attributes yet.</div>

                    <div class="vx-flex vx-flex-col vx-gap-4">
                        <div
                            v-for="customAttribute in customAttributes"
                            :key="customAttribute.key"
                            class="vx-flex vx-flex-col vx-gap-1"
                        >
                            <strong>{{ customAttribute.key }}:</strong>
                            <div class="vx-text-sm vx-text-slate-500 vx-break-all">
                                {{ customAttribute.value }}
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>
        </div>
    </kiosk-team-details>
</template>

<script setup>
import KioskTeamDetails from '~/components/kiosk/kiosk-teams/KioskTeamDetails.vue';
import { VxBadge, VxButton, VxExpandableCard, VxIconLoading, VxPopover } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import { useTeamDetails } from '~/components/kiosk/kiosk-teams/useTeamDetails';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSquareArrowUpRight, faTheaterMasks } from '@fortawesome/pro-solid-svg-icons';
import { formatPhoneNumber } from '~/components/filters';
import { countryEmoji } from '~/utils/string';
import { NUMBER_TYPES } from '~/constants/phoneNumbers';

const currentUserId = Spark.state.user.id;

const {
    team,
    onMountedTeam,

    owner,
    ownerLoading,
    getOwner,

    parent,
    parentLoading,
    getParent,

    usersCount,
    usersCountLoading,
    getUsersCount,

    invitationsCount,
    invitationsCountLoading,
    getInvitationsCount,

    teamsCount,
    teamsCountLoading,
    getTeamsCount,

    groupsCount,
    groupsCountLoading,
    getGroupsCount,

    phoneNumbersCount,
    phoneNumbersCountLoading,
    getPhoneNumbersCount,

    phoneNumbers,
    phoneNumbersLoading,
    getPhoneNumbers,

    customAttributes,
    customAttributesLoading,
    getCustomAttributes,
} = useTeamDetails();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

onMountedTeam(() => {
    getOwner();
    getParent();
    getUsersCount();
    getInvitationsCount();

    if (team.value.organization_id) {
        getTeamsCount();
    }

    // eslint-disable-next-line no-restricted-syntax
    if (team.value.team_attributes?.contact_groups) {
        getGroupsCount();
    }

    getPhoneNumbersCount();
    getPhoneNumbers({
        per_page: 5,
    });

    getCustomAttributes();
});
</script>
